import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowClockwise, FileArrowUp, Warning, CaretLeft } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/Layout";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export default function AdminCsvSyncHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/csv-sync-history?limit=100")
      .then(r => setRows(r.data.items || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <AdminShell>
      <div className="p-4 sm:p-8" data-testid="csv-sync-history-page">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/admin/jobs" className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-xs font-bold uppercase tracking-wider">
            <CaretLeft weight="bold" size={14}/> Jobs
          </Link>
        </div>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h1 className="font-display font-black text-3xl tracking-tight">CSV Sync History</h1>
            <p className="text-slate-500 text-sm mt-1">
              Every AppFolio / Kraaft import run with per-run breakdown so you can prove which WOs came from which upload.
            </p>
          </div>
          <button onClick={load} data-testid="refresh-btn"
            className="h-9 px-3 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5">
            <ArrowClockwise weight="bold" size={14}/> Refresh
          </button>
        </div>

        {loading && <div className="text-slate-500 py-8 text-center text-sm">Loading…</div>}

        {!loading && rows.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="empty-state">
            <FileArrowUp weight="fill" size={36} className="mx-auto text-slate-400"/>
            <div className="mt-2 font-black uppercase text-sm tracking-wider text-slate-500">No imports yet</div>
            <div className="text-xs text-slate-400 mt-1">Head to Jobs → Ops Toolkit → Import CSV to run your first sync.</div>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="space-y-2">
            {rows.map(r => {
              const isOpen = expanded === r.id;
              const total = r.created + r.updated + r.closed + r.skipped_dupes;
              return (
                <div key={r.id} data-testid={`sync-row-${r.id}`}
                  className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                  <button onClick={() => setExpanded(isOpen ? null : r.id)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-black text-slate-900 truncate">{r.filename || "unnamed.csv"}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {fmtDate(r.ran_at)} · by {r.ran_by} · {total} rows processed
                      </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 shrink-0">
                      <Stat label="New" val={r.created} tone="emerald"/>
                      <Stat label="Updated" val={r.updated} tone="cyan"/>
                      <Stat label="Closed" val={r.closed} tone="violet"/>
                      <Stat label="Same" val={r.skipped_dupes} tone="slate"/>
                      {r.error_count > 0 && <Stat label="Errors" val={r.error_count} tone="red"/>}
                    </div>
                  </button>
                  {/* mobile stats row */}
                  <div className="sm:hidden grid grid-cols-4 gap-1 px-4 pb-3 -mt-1">
                    <MobileStat label="New" val={r.created} tone="emerald"/>
                    <MobileStat label="Upd" val={r.updated} tone="cyan"/>
                    <MobileStat label="Closed" val={r.closed} tone="violet"/>
                    <MobileStat label="Same" val={r.skipped_dupes} tone="slate"/>
                  </div>
                  {isOpen && r.errors_preview.length > 0 && (
                    <div className="border-t border-slate-100 bg-red-50 px-4 py-3">
                      <div className="text-[11px] font-black uppercase tracking-widest text-red-700 flex items-center gap-1 mb-1">
                        <Warning weight="fill" size={12}/> {r.error_count} rows skipped
                      </div>
                      <ul className="text-xs text-slate-700 list-disc pl-4 space-y-0.5">
                        {r.errors_preview.map((er, i) => (
                          <li key={i}>Row {er.row}: {er.error}</li>
                        ))}
                        {r.error_count > r.errors_preview.length && (
                          <li className="italic text-slate-500">… and {r.error_count - r.errors_preview.length} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminShell>
  );
}

function Stat({ label, val, tone }) {
  const tones = {
    emerald: "text-emerald-700",
    cyan: "text-cyan-700",
    violet: "text-violet-700",
    slate: "text-slate-500",
    red: "text-red-700",
  };
  return (
    <div className="text-center min-w-[52px]">
      <div className={`text-xl font-black tabular-nums ${tones[tone]}`}>{val}</div>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">{label}</div>
    </div>
  );
}
function MobileStat({ label, val, tone }) {
  const tones = { emerald: "text-emerald-700", cyan: "text-cyan-700", violet: "text-violet-700", slate: "text-slate-500" };
  return (
    <div className="text-center">
      <div className={`text-base font-black tabular-nums ${tones[tone]}`}>{val}</div>
      <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">{label}</div>
    </div>
  );
}
